import { useEffect, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import { Button, buttonClasses } from '@/design-system/Button'
import { LanguageBar } from '@/design-system/LanguageSwitcher'
import { Container, Overline, Section, SectionTitle, Skeleton } from '@/design-system/primitives'
import { LegalLink } from '@/features/event/components/LegalReader'
import { usePublicEvent } from '@/features/event/api'
import { readBooleanSetting } from '@/features/event/types'
import { useRubrics } from '@/features/event/useRubrics'
import { PublicLayout } from '@/layouts/PublicLayout'
import { usePublicMenu } from '@/features/food/api'
import { FoodPicker } from '@/features/food/components'
import { usePublicDrinks } from '@/features/tables/api'
import { EMPTY_TABLE, TableComposer, type TableSelection } from '@/features/tables/components'
import { ApiError } from '@/lib/apiClient'
import { formatMoney } from '@/lib/money'
import { isInternationalNumber, looksLikeEmail } from '@/lib/phone'

import {
  useCreateOrder,
  useQuote,
  usePublicTicketTypes,
  type FoodSelection,
  type Selection,
} from '../api'
import {
  AttendeeFields,
  Checkbox,
  Field,
  PaymentChoiceField,
  PromoCodeField,
  QuoteSummary,
  SinglePreferenceField,
  TextInput,
  TicketTypeCard,
} from '../components'
import type { AttendeeInput, PaymentChoice, Quote, SinglePreference } from '../types'

const EMPTY_ATTENDEE: AttendeeInput = { first_name: '' }

type ContactField = 'first_name' | 'last_name' | 'email' | 'whatsapp' | 'guests'

export function TicketsPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()

  const eventQuery = usePublicEvent()
  const event = eventQuery.data
  const ticketTypesQuery = usePublicTicketTypes(event?.slug)
  const menuQuery = usePublicMenu(event?.slug)
  const drinksQuery = usePublicDrinks(event?.slug)

  const [quantities, setQuantities] = useState<Record<string, number>>({})
  /**
   * Trois portes d'entrée, un seul tunnel.
   *
   * Un billet d'entrée, un plat et une table sont **trois envies distinctes**.
   * Obliger celui qui veut réserver une table à traverser d'abord la
   * billetterie puis la carte lui fait croire qu'il doit acheter les trois —
   * et il abandonne. Chaque porte ouvre donc directement sur ce qu'il est venu
   * chercher.
   *
   * Le tunnel reste unique parce que la commande, elle, l'est : une table avec
   * des plats est une seule commande, un seul paiement, une seule
   * confirmation. Ce sont les *chemins* qui se séparent, pas la vente.
   */
  const { navItems } = useRubrics()
  const [params] = useSearchParams()
  const wantedTable = params.get('table')
  const wantedFood = params.get('food')
  // « Commander à manger » depuis Mon Masked Night ou la page de commande :
  // le numéro de la commande d'entrée voyage avec le lien, pour que le client
  // n'ait plus qu'à confirmer son adresse.
  const wantedRef = params.get('ref')
  // « ?drinks=<id>:2,<id>:1 » — la carte des bouteilles sur commande passe la
  // main au tunnel comme une table le fait avec « ?table=… ». Une sélection
  // illisible est ignorée plutôt que de faire échouer la page : on préfère un
  // panier vide à un écran blanc.
  const wantedDrinks = useMemo(() => {
    const raw = params.get('drinks')
    if (!raw) return []
    return raw
      .split(',')
      .map((chunk) => {
        const [id, quantity] = chunk.split(':')
        return { drink_item_id: id ?? '', quantity: Number(quantity ?? 0) }
      })
      .filter((row) => row.drink_item_id && Number.isFinite(row.quantity) && row.quantity > 0)
  }, [params])
  // Mémorisée au montage : `params` ne bouge plus, mais l'intention « je viens
  // pour ça » doit survivre aux changements d'étape.
  const [entry] = useState<'tickets' | 'food' | 'table'>(
    wantedTable ? 'table' : wantedFood ? 'food' : 'tickets',
  )
  // Les bouteilles n'ouvrent pas d'étape à elles : on arrive avec sa
  // sélection déjà faite depuis la carte, et il ne reste qu'à se nommer.
  const [drinkSelection] = useState(wantedDrinks)
  const startedAtTable = entry === 'table'

  const [step, setStep] = useState<'select' | 'food' | 'table' | 'details'>(
    entry === 'table' ? 'table' : entry === 'food' ? 'food' : 'select',
  )
  // Clés `item:<id>` / `pack:<id>` : une seule table pour les deux familles.
  const [foodQuantities, setFoodQuantities] = useState<Record<string, number>>({})
  const [table, setTable] = useState<TableSelection>(
    wantedTable ? { ...EMPTY_TABLE, table_type_id: wantedTable } : EMPTY_TABLE,
  )
  const [quote, setQuote] = useState<Quote | null>(null)
  // Les **accompagnants** seulement : l'acheteur occupe la première place et
  // n'a pas à se saisir lui-même. Vide pour un billet solo.
  const [attendees, setAttendees] = useState<AttendeeInput[]>([])
  // L'acheteur : nom, prénom et e-mail obligatoires, WhatsApp facultatif.
  // C'est le contact de la commande — celui qui reçoit le QR, les consignes
  // de paiement et, après la soirée, le remerciement.
  const [customer, setCustomer] = useState({
    first_name: '',
    last_name: '',
    email: '',
    whatsapp: '',
  })
  // Les champs refusés, à afficher sous chacun d'eux. Le navigateur arrête
  // un champ vide, pas un nom fait d'espaces ni un numéro sans indicatif.
  const [invalid, setInvalid] = useState<Partial<Record<ContactField, string>>>({})
  // La question du bracelet vaut aussi pour l'acheteur : elle est portée à part
  // puisqu'il n'a plus de carte « participant » à lui.
  const [buyerPreference, setBuyerPreference] = useState<SinglePreference | undefined>()
  const [consents, setConsents] = useState({ terms: false, privacy: false, marketing: false })
  // Comment le client compte régler. Annoncé avant tout paiement : c'est
  // ce qui distingue, la veille, une commande attendue à l'entrée d'un
  // panier abandonné.
  const [paymentChoice, setPaymentChoice] = useState<PaymentChoice | null>(null)
  // Tout regler, ou reserver en payant la premiere tranche. Le choix
  // n'apparait que si le serveur l'accorde : proposer un echelonnement
  // pour le refuser ensuite serait une fausse promesse.
  const [payWhole, setPayWhole] = useState(true)
  const money = (minor: number) =>
    formatMoney({ amount_minor: minor, currency: quote?.currency ?? 'EUR' }, i18n.language)
  // Le serveur parle français ; un refus que l'on sait nommer est traduit ici.
  // « Vente fermée » arrive quand l'équipe ferme la vente pendant qu'un
  // panier est ouvert : le message doit être lisible dans la langue du client.
  const describe = (cause: unknown) =>
    cause instanceof ApiError
      ? cause.code === 'SALES_CLOSED'
        ? t('ticketing.salesClosed.error')
        : ADMISSION_ERRORS[cause.code]
          ? t(ADMISSION_ERRORS[cause.code] as string)
          : cause.code === 'VALIDATION_ERROR' && cause.details.length > 0
            ? t('ticketing.validation.summary')
            : cause.message
      : String(cause)

  /** Ce qui manque ou ne va pas, champ par champ, dans la langue du client. */
  function check(): Partial<Record<ContactField, string>> {
    const found: Partial<Record<ContactField, string>> = {}
    if (!customer.first_name.trim()) found.first_name = t('ticketing.validation.firstName')
    if (!customer.last_name.trim()) found.last_name = t('ticketing.validation.lastName')
    if (!looksLikeEmail(customer.email)) found.email = t('ticketing.validation.email')
    if (customer.whatsapp.trim() && !isInternationalNumber(customer.whatsapp))
      found.whatsapp = t('ticketing.validation.whatsapp')
    if (attendees.some((attendee) => !attendee.first_name.trim()))
      found.guests = t('ticketing.validation.guestFirstName')
    return found
  }

  /** Le refus du serveur, ramené aux mêmes champs que la vérification locale. */
  function fromServer(cause: unknown): Partial<Record<ContactField, string>> {
    if (!(cause instanceof ApiError) || cause.code !== 'VALIDATION_ERROR') return {}
    const found: Partial<Record<ContactField, string>> = {}
    for (const detail of cause.details) {
      const field = String((detail as { field?: unknown }).field ?? '')
      if (field === 'customer.first_name') found.first_name = t('ticketing.validation.firstName')
      else if (field === 'customer.last_name') found.last_name = t('ticketing.validation.lastName')
      else if (field === 'customer.email') found.email = t('ticketing.validation.email')
      else if (field === 'customer.whatsapp') found.whatsapp = t('ticketing.validation.whatsapp')
      else if (/^attendees\.\d+\.first_name$/.test(field))
        found.guests = t('ticketing.validation.guestFirstName')
    }
    return found
  }
  const [promoCode, setPromoCode] = useState('')
  // « J'ai déjà mon billet » : le numéro saisi, et celui que le serveur a
  // reconnu pour l'adresse donnée. Seul le second ouvre la validation.
  const [admissionNumber, setAdmissionNumber] = useState(wantedRef ?? '')
  const [admissionVerified, setAdmissionVerified] = useState<string | null>(null)
  // Le serveur dit si les plats s'AJOUTERONT à cette commande (même numéro,
  // même code d'entrée) : c'est ce que l'écran annonce avant de payer.
  const [admissionAppend, setAdmissionAppend] = useState(false)
  const [admissionError, setAdmissionError] = useState<string | null>(null)
  const [checkingAdmission, setCheckingAdmission] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Un avis, pas une erreur : le devis a été refait et le total a peut-être
  // bougé. Le client doit le voir avant de renvoyer.
  const [notice, setNotice] = useState<string | null>(null)
  // Une clé par devis, tirée une fois : un double-clic ou un renvoi après une
  // coupure repart avec la même, et le serveur rend la commande déjà créée.
  // Un nouveau devis — prix changé, panier modifié — en tire une nouvelle.
  const idempotencyKey = useMemo(() => crypto.randomUUID(), [quote?.quote_id])

  // Le code promo n'existe que si l'équipe en a ouvert un. Le champ suit le
  // réglage : le jour où un code est créé, on coche la case et il réapparaît
  // sans qu'on touche au code.
  const promoEnabled = readBooleanSetting(event?.settings ?? {}, 'promo.enabled', false)

  // Une option fermée en back-office ne doit pas s'afficher : la proposer
  // pour la refuser ensuite serait une fausse promesse.
  const paymentChoices = useMemo<PaymentChoice[]>(() => {
    const settings = event?.settings ?? {}
    return (
      [
        ['paypal_link', 'payment.allow_paypal_link'],
        ['cash_at_door', 'payment.allow_cash_at_door'],
        ['paypal_at_door', 'payment.allow_paypal_at_door'],
      ] as const
    )
      .filter(([, code]) => readBooleanSetting(settings, code, false))
      .map(([choice]) => choice)
  }, [event?.settings])

  const quoteMutation = useQuote(event?.id)
  const orderMutation = useCreateOrder()

  const selection: Selection[] = useMemo(
    () =>
      Object.entries(quantities)
        .filter(([, quantity]) => quantity > 0)
        .map(([ticket_type_id, quantity]) => ({ ticket_type_id, quantity })),
    [quantities],
  )

  const foodSelection: FoodSelection[] = useMemo(
    () =>
      Object.entries(foodQuantities)
        .filter(([, quantity]) => quantity > 0)
        .map(([key, quantity]) => {
          const [kind, ref_id] = key.split(':')
          return { kind: kind === 'pack' ? 'pack' : 'item', ref_id: ref_id ?? '', quantity }
        }),
    [foodQuantities],
  )

  const tablePayload = useMemo(() => {
    if (!table.table_type_id) return null
    return {
      table_type_id: table.table_type_id,
      party_size: table.party_size,
      quantity: table.quantity,
      package_id: table.package_id,
      items: Object.entries(table.items)
        .filter(([, quantity]) => quantity > 0)
        .map(([drink_item_id, quantity]) => ({ drink_item_id, quantity })),
    }
  }, [table])

  // Le panier n'est jamais valorisé ici : à chaque changement, on redemande au
  // serveur ce que cela coûte. Un léger délai évite d'inonder l'API pendant
  // qu'on clique sur « + ».
  const { mutateAsync: requestQuote } = quoteMutation
  const quotePayload = useMemo(
    () => ({
      tickets: selection,
      food: foodSelection,
      drinks: drinkSelection,
      drink_table: tablePayload,
      promo_code: promoCode || null,
    }),
    [selection, foodSelection, drinkSelection, tablePayload, promoCode],
  )
  useEffect(() => {
    if (
      !event ||
      (selection.length === 0 &&
        foodSelection.length === 0 &&
        drinkSelection.length === 0 &&
        tablePayload === null)
    ) {
      setQuote(null)
      return
    }
    const timer = window.setTimeout(() => {
      requestQuote(quotePayload)
        .then((result) => {
          setQuote(result)
          setError(null)
          setNotice(null)
        })
        .catch((cause: unknown) => {
          setQuote(null)
          setError(describe(cause))
        })
    }, 350)
    return () => window.clearTimeout(timer)
  }, [event, selection, foodSelection, drinkSelection, tablePayload, quotePayload, requestQuote])

  // Autant de champs que d'**accompagnants** — c'est le serveur qui donne ce
  // nombre, à partir de `admits_count`, et il en retire déjà l'acheteur.
  useEffect(() => {
    if (!quote) return
    setAttendees((current) =>
      Array.from(
        { length: quote.extra_attendees_required },
        (_, index) => current[index] ?? { ...EMPTY_ATTENDEE },
      ),
    )
  }, [quote])

  // Le numéro reconnu l'a été pour UNE adresse : en changer, c'est devoir le
  // faire reconnaître à nouveau — le serveur, lui, revérifiera à la commande.
  useEffect(() => {
    setAdmissionVerified(null)
  }, [customer.email])

  // Un champ corrigé perd son message aussitôt : le laisser affiché jusqu'au
  // prochain envoi ferait croire que la correction n'a pas pris. On ne fait
  // que retirer — un champ encore jamais signalé ne s'allume pas en cours de
  // frappe.
  useEffect(() => {
    setInvalid((current) => {
      if (Object.keys(current).length === 0) return current
      const now = check()
      const kept: Partial<Record<ContactField, string>> = {}
      for (const key of Object.keys(current) as ContactField[]) {
        if (now[key]) kept[key] = now[key]
      }
      return kept
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer, attendees])

  /** Fait reconnaître la commande d'entrée citée, avec l'adresse saisie. */
  async function verifyAdmission() {
    setAdmissionError(null)
    const number = admissionNumber.trim()
    const email = customer.email.trim()
    if (!number) {
      setAdmissionError(t('admission.numberMissing'))
      return
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setAdmissionError(t('admission.emailFirst'))
      return
    }
    setCheckingAdmission(true)
    try {
      const checked = await requestQuote({
        ...quotePayload,
        admission: { order_number: number, email },
      })
      setAdmissionVerified(checked.admission_order_number ?? null)
      setAdmissionAppend(Boolean(checked.admission_append))
    } catch (cause) {
      setAdmissionVerified(null)
      setAdmissionAppend(false)
      setAdmissionError(describe(cause))
    } finally {
      setCheckingAdmission(false)
    }
  }

  async function submit(submitEvent: React.FormEvent) {
    submitEvent.preventDefault()
    if (!quote) return
    setError(null)
    setNotice(null)
    if (quote.admission_required && !admissionVerified) {
      setError(t('admission.required'))
      return
    }

    const problems = check()
    setInvalid(problems)
    if (Object.keys(problems).length > 0) {
      setError(t('ticketing.validation.summary'))
      return
    }

    const contact = {
      first_name: customer.first_name.trim(),
      last_name: customer.last_name.trim(),
      email: customer.email.trim(),
      ...(customer.whatsapp.trim() ? { whatsapp: customer.whatsapp.trim() } : {}),
      locale: i18n.language,
    }

    try {
      const order = await orderMutation.mutateAsync({
        idempotencyKey,
        quote_id: quote.quote_id,
        quote_hash: quote.quote_hash,
        customer: contact,
        // L'acheteur ouvre la liste : le serveur attend toujours autant
        // d'identités que de places, on lui rend la sienne sans la lui avoir
        // fait retaper. Les accompagnants suivent, avec leur seul prénom.
        attendees:
          quote.attendees_required > 0
            ? [
                {
                  first_name: contact.first_name,
                  last_name: contact.last_name,
                  email: contact.email,
                  ...(buyerPreference ? { single_preference: buyerPreference } : {}),
                },
                ...attendees.map((attendee) => ({
                  first_name: attendee.first_name.trim(),
                  ...(attendee.single_preference
                    ? { single_preference: attendee.single_preference }
                    : {}),
                })),
              ]
            : [],
        consents,
        ...(paymentChoice ? { payment_choice: paymentChoice } : {}),
        ...(quote?.instalments?.offered && !payWhole
          ? { payment_plan: 'installments' as const }
          : {}),
        ...(quote.admission_required && admissionVerified
          ? { admission_order_number: admissionVerified }
          : {}),
      })
      // Paiement en ligne qui se confirme seul (PayPal par API) : le client
      // doit approuver chez PayPal, sinon rien ne sera jamais débité. On l'y
      // envoie directement ; PayPal le ramènera sur sa page de commande.
      // Un lien PayPal.me (mode manuel) n'est pas suivi d'office : il faut
      // d'abord lire la référence à recopier, que montre la page de commande.
      if (order.payment?.confirms_automatically && order.payment.redirect_url) {
        window.location.assign(order.payment.redirect_url)
        return
      }
      // Les consignes de règlement ne transitent pas par l'URL : elles sont
      // remises une seule fois, à la création de la commande.
      //
      // Des plats ajoutés à une réservation : la page de suivi est celle de
      // la réservation (même numéro), ouverte avec le jeton du seul lot — le
      // lien complet part dans la boîte du titulaire.
      const access = order.supplement?.access_token ?? order.access_token ?? ''
      navigate(`/commande/${order.id}?token=${encodeURIComponent(access)}`, {
        state: { payment: order.payment },
      })
    } catch (cause) {
      // Devis périmé, ou prix qui a bougé entre-temps : le client n'y est
      // pour rien, et lui dire « recommencez » le ferait abandonner. On
      // redemande le devis et on lui montre le nouveau total ; il n'a plus
      // qu'à confirmer.
      if (
        cause instanceof ApiError &&
        (cause.code === 'QUOTE_EXPIRED' || cause.code === 'PRICE_CHANGED')
      ) {
        try {
          const fresh = await requestQuote(quotePayload)
          setQuote(fresh)
          setNotice(
            fresh.total_minor === quote.total_minor
              ? t('ticketing.quoteRefreshed')
              : t('ticketing.quoteRefreshedNewTotal', {
                  amount: formatMoney(
                    { amount_minor: fresh.total_minor, currency: fresh.currency },
                    i18n.language,
                  ),
                }),
          )
          return
        } catch (refreshCause) {
          setError(describe(refreshCause))
          return
        }
      }
      setInvalid(fromServer(cause))
      setError(describe(cause))
    }
  }

  if (eventQuery.isPending || ticketTypesQuery.isPending) {
    return (
      <Container className="py-32">
        <Skeleton className="h-8 w-56" />
        <Skeleton className="mt-6 h-40 w-full" />
      </Container>
    )
  }

  if (!event) {
    return (
      <>
        <LanguageBar />
        <Container className="py-32">
          <h1 className="font-display text-display-lg">{t('state.noEventTitle')}</h1>
        </Container>
      </>
    )
  }

  // « Ventes ouvertes » décoché : la page ne propose rien à acheter. Le
  // serveur refuserait de toute façon (`SALES_CLOSED`) ; laisser composer un
  // panier pour le refuser à la fin serait une fausse promesse. Après la
  // soirée, on dit « terminée » plutôt que « pas encore ouverte ».
  if (!readBooleanSetting(event.settings, 'sales.open', false)) {
    const ended = new Date(event.ends_at ?? event.starts_at).getTime() < Date.now()
    return (
      <PublicLayout
        eventName={event.name}
        logoUrl={event.logo_url}
        availableLocales={event.locales}
        navItems={navItems}
      >
        <Section className="pt-28">
          <Overline>{t('ticketing.title')}</Overline>
          <SectionTitle>
            {ended ? t('ticketing.salesClosed.endedTitle') : t('ticketing.salesClosed.notYetTitle')}
          </SectionTitle>
          <p role="status" className="mt-4 max-w-2xl text-content-secondary">
            {ended ? t('ticketing.salesClosed.endedBody') : t('ticketing.salesClosed.notYetBody')}
          </p>
          <Link to="/" className={buttonClasses({ variant: 'outline', className: 'mt-8' })}>
            {t('ticketing.salesClosed.home')}
          </Link>
        </Section>
      </PublicLayout>
    )
  }

  const ticketTypes = ticketTypesQuery.data ?? []
  const menu = menuQuery.data
  // Les étapes facultatives n'apparaissent que si le catalogue existe.
  const hasMenu = Boolean(menu && (menu.items.length > 0 || menu.packs.length > 0))

  // **Trois portes distinctes, et elles ne se croisent pas.**
  //
  // Prendre un billet d'entrée, commander à manger et réserver une table sont
  // trois décisions séparées. Enchaîner l'une sur l'autre transformait un
  // achat de billet en parcours de vente : on venait pour une entrée, on se
  // retrouvait devant la carte, puis devant les tables.
  //
  // Chaque parcours va donc droit à ses coordonnées. Seule la réservation
  // d'une table propose encore d'y ajouter à manger : c'est un service pour
  // qui a déjà sa table, pas une porte de plus.
  const afterSelect = 'details'
  const afterFood = 'details'

  return (
    <PublicLayout
      eventName={event?.name}
      logoUrl={event?.logo_url}
      availableLocales={event?.locales}
      navItems={navItems}
    >
    <Section className="pt-28">
      <Overline>{t('ticketing.title')}</Overline>
      <SectionTitle>
        {step === 'select'
          ? t('ticketing.chooseTitle')
          : step === 'food'
            ? t('menu.stepTitle')
            : step === 'table'
              ? t('tables.build')
              : t('ticketing.yourDetails')}
      </SectionTitle>
      <p className="mt-4 max-w-2xl text-content-secondary">
        {step === 'food'
          ? t('menu.stepBody')
          : step === 'table'
            ? t('tables.buildBody')
            : t('ticketing.chooseBody')}
      </p>

      {/* Dit dès la carte, pas seulement au moment de payer : celui qui n'a
          pas encore d'entrée doit le savoir avant de composer son repas. */}
      {step === 'food' && quote?.admission_required ? (
        <p
          role="status"
          className="mt-6 max-w-2xl rounded-md border border-champagne-300/40 bg-bg-overlay p-4 text-sm text-content-primary"
        >
          {t('admission.foodStepNotice')}
        </p>
      ) : null}

      {error ? (
        <p
          role="alert"
          className="mt-6 rounded-md border border-state-danger/40 bg-bg-overlay p-4 text-sm text-content-primary"
        >
          {error}
        </p>
      ) : null}

      {notice ? (
        <p
          role="status"
          className="mt-6 rounded-md border border-champagne-300/40 bg-bg-overlay p-4 text-sm text-content-primary"
        >
          {notice}
        </p>
      ) : null}

      <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] lg:items-start">
        <div className="space-y-4">
          {step === 'select' ? (
            ticketTypes.map((ticketType) => (
              <TicketTypeCard
                key={ticketType.id}
                ticketType={ticketType}
                quantity={quantities[ticketType.id] ?? 0}
                locale={i18n.language}
                onChange={(value) =>
                  setQuantities((current) => ({ ...current, [ticketType.id]: value }))
                }
              />
            ))
          ) : step === 'food' && menuQuery.data ? (
            <FoodPicker
              menu={menuQuery.data}
              quantities={foodQuantities}
              locale={i18n.language}
              onChange={(key, value) =>
                setFoodQuantities((current) => ({ ...current, [key]: value }))
              }
            />
          ) : step === 'table' && drinksQuery.data ? (
            <TableComposer
              catalog={drinksQuery.data}
              selection={table}
              onChange={setTable}
              locale={i18n.language}
              allowCustomization={readBooleanSetting(
                event?.settings ?? {},
                'tables.allow_customization',
                true,
              )}
            />
          ) : (
            <form
              id="checkout"
              noValidate
              onSubmit={(formEvent) => void submit(formEvent)}
              className="space-y-6"
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Field label={t('ticketing.firstName')}>
                    <TextInput
                      required
                      autoComplete="given-name"
                      aria-invalid={invalid.first_name ? true : undefined}
                      value={customer.first_name}
                      onChange={(changeEvent) =>
                        setCustomer({ ...customer, first_name: changeEvent.target.value })
                      }
                    />
                  </Field>
                  <FieldError message={invalid.first_name} />
                </div>
                <div>
                  <Field label={t('ticketing.lastName')}>
                    <TextInput
                      required
                      autoComplete="family-name"
                      aria-invalid={invalid.last_name ? true : undefined}
                      value={customer.last_name}
                      onChange={(changeEvent) =>
                        setCustomer({ ...customer, last_name: changeEvent.target.value })
                      }
                    />
                  </Field>
                  <FieldError message={invalid.last_name} />
                </div>
              </div>

              <div>
                <Field label={t('ticketing.email')}>
                  <TextInput
                    required
                    type="email"
                    autoComplete="email"
                    inputMode="email"
                    aria-invalid={invalid.email ? true : undefined}
                    value={customer.email}
                    onChange={(changeEvent) =>
                      setCustomer({ ...customer, email: changeEvent.target.value })
                    }
                  />
                </Field>
                <FieldError message={invalid.email} />
              </div>

              {quote?.admission_required ? (
                <AdmissionBlock
                  number={admissionNumber}
                  onNumberChange={(value) => {
                    setAdmissionNumber(value)
                    setAdmissionVerified(null)
                    setAdmissionAppend(false)
                    setAdmissionError(null)
                  }}
                  verified={admissionVerified}
                  appended={admissionAppend}
                  error={admissionError}
                  checking={checkingAdmission}
                  onVerify={() => void verifyAdmission()}
                  onAddTicket={ticketTypes.length > 0 ? () => setStep('select') : null}
                  onAddTable={
                    (drinksQuery.data?.table_types.length ?? 0) > 0
                      ? () => setStep('table')
                      : null
                  }
                />
              ) : null}

              <div>
                <Field label={t('ticketing.whatsapp')} hint={t('ticketing.optional')}>
                  <TextInput
                    type="tel"
                    autoComplete="tel"
                    inputMode="tel"
                    placeholder="+49 170 1234567"
                    aria-invalid={invalid.whatsapp ? true : undefined}
                    value={customer.whatsapp}
                    onChange={(changeEvent) =>
                      setCustomer({ ...customer, whatsapp: changeEvent.target.value })
                    }
                  />
                </Field>
                {invalid.whatsapp ? (
                  <FieldError message={invalid.whatsapp} />
                ) : (
                  <p className="mt-1.5 text-xs text-content-muted">
                    {t('ticketing.whatsappHelp')}
                  </p>
                )}
              </div>

              {/* Le bracelet se demande pour soi, même quand on vient seul :
                  la question suit donc l'acheteur, pas la liste des invités. */}
              {quote && quote.attendees_required > 0 ? (
                <SinglePreferenceField
                  value={buyerPreference}
                  onChange={setBuyerPreference}
                />
              ) : null}

              {/* Un billet solo ne demande rien de plus : l'acheteur EST le
                  participant. On n'affiche cette section que s'il y a
                  réellement quelqu'un d'autre à nommer. */}
              {attendees.length > 0 ? (
                <>
                  <div>
                    <Overline>{t('ticketing.guests')}</Overline>
                    <p className="mt-2 text-sm text-content-secondary">
                      {t('ticketing.guestsHelp', { count: attendees.length })}
                    </p>
                  </div>

                  <FieldError message={invalid.guests} />

                  {attendees.map((attendee, index) => (
                    <AttendeeFields
                      key={index}
                      index={index}
                      attendee={attendee}
                      onChange={(updated) =>
                        setAttendees((current) =>
                          current.map((item, position) =>
                            position === index ? updated : item,
                          ),
                        )
                      }
                    />
                  ))}
                </>
              ) : null}

              <PaymentChoiceField
                value={paymentChoice}
                onChange={setPaymentChoice}
                available={paymentChoices}
              />

              {quote?.instalments?.offered ? (
                <fieldset className="border-t border-line-subtle pt-4">
                  <legend className="text-caption uppercase tracking-wide text-content-muted">
                    {t('payment.instalments.legend')}
                  </legend>
                  <div className="mt-3 grid gap-2">
                    <label className="flex items-start gap-3">
                      <input
                        type="radio"
                        name="instalments"
                        checked={payWhole}
                        onChange={() => setPayWhole(true)}
                        className="mt-1 h-4 w-4 shrink-0 accent-[var(--ember-500)]"
                      />
                      <span className="text-sm text-content-primary">
                        {t('payment.instalments.whole', {
                          amount: money(quote.total_minor),
                        })}
                      </span>
                    </label>
                    <label className="flex items-start gap-3">
                      <input
                        type="radio"
                        name="instalments"
                        checked={!payWhole}
                        onChange={() => setPayWhole(false)}
                        className="mt-1 h-4 w-4 shrink-0 accent-[var(--ember-500)]"
                      />
                      <span className="text-sm text-content-primary">
                        {t('payment.instalments.deposit', {
                          first: money(quote.instalments.first_minor),
                          second: money(quote.instalments.second_minor),
                        })}
                        <span className="mt-0.5 block text-caption text-content-muted">
                          {t('payment.instalments.hold')}
                        </span>
                      </span>
                    </label>
                  </div>
                </fieldset>
              ) : null}

              {/* Une case dont on ne peut pas lire le texte ne recueille
                  rien : ni un consentement valable, ni la confiance de celui
                  qui coche. Le document s'ouvre par-dessus, panier intact. */}
              <div className="border-t border-line-subtle pt-4">
                <Checkbox
                  checked={consents.terms}
                  onChange={(value) => setConsents({ ...consents, terms: value })}
                >
                  <Trans
                    i18nKey="ticketing.consentTerms"
                    components={{
                      terms: <LegalLink blockKey="legal.terms" label={t('legal.terms')} />,
                    }}
                  />
                </Checkbox>
                <Checkbox
                  checked={consents.privacy}
                  onChange={(value) => setConsents({ ...consents, privacy: value })}
                >
                  <Trans
                    i18nKey="ticketing.consentPrivacy"
                    components={{
                      privacy: (
                        <LegalLink blockKey="legal.privacy" label={t('legal.privacy')} />
                      ),
                    }}
                  />
                </Checkbox>
                <Checkbox
                  checked={consents.marketing}
                  onChange={(value) => setConsents({ ...consents, marketing: value })}
                >
                  {t('ticketing.consentMarketing')}
                </Checkbox>
              </div>
            </form>
          )}
        </div>

        <QuoteSummary quote={quote} locale={i18n.language}>
          {/* Fermé tant qu'aucun code n'existe : un champ « Code promo » offert
              à quelqu'un qui n'en a pas lui fait croire qu'il rate une remise,
              et l'envoie chercher sur le web au lieu de finir son achat. */}
          {promoEnabled ? (
            <PromoCodeField value={promoCode} onApply={setPromoCode} />
          ) : null}

          {step === 'select' ? (
            <Button
              className="w-full"
              size="lg"
              disabled={!quote || quote.lines.length === 0}
              onClick={() => setStep(afterSelect)}
            >
              {t('ticketing.continue')}
            </Button>
          ) : step === 'food' ? (
            <div className="space-y-3">
              <Button className="w-full" size="lg" onClick={() => setStep(afterFood)}>
                {t('ticketing.continue')}
              </Button>
              {/* Entré par la carte, on n'a pas traversé la billetterie :
                  y « revenir » n'aurait aucun sens. */}
              {entry === 'tickets' ? (
                <Button variant="ghost" className="w-full" onClick={() => setStep('select')}>
                  {t('ticketing.back')}
                </Button>
              ) : (
                <Link
                  to="/carte"
                  className={buttonClasses({
                    variant: 'ghost',
                    size: 'lg',
                    className: 'w-full',
                  })}
                >
                  {t('tables.backToMenu')}
                </Link>
              )}
            </div>
          ) : step === 'table' ? (
            <div className="space-y-3">
              <Button className="w-full" size="lg" onClick={() => setStep('details')}>
                {t('ticketing.continue')}
              </Button>
              {/* On réserve une table, et souvent on mange. Le proposer ici
                  évite de repasser par la carte et de recommencer. */}
              {startedAtTable && hasMenu ? (
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setStep('food')}
                >
                  {t('ticketing.addFood')}
                </Button>
              ) : null}
              {/* Arrivé depuis « Réserver cette table », on n'a traversé ni la
                  billetterie ni la carte : remonter ces étapes n'aurait aucun
                  sens. On repart d'où l'on vient. */}
              {startedAtTable ? (
                <Link
                  to="/carte"
                  className={buttonClasses({
                    variant: 'ghost',
                    size: 'lg',
                    className: 'w-full',
                  })}
                >
                  {t('tables.backToMenu')}
                </Link>
              ) : (
                <Button
                  variant="ghost"
                  className="w-full"
                  onClick={() => setStep('select')}
                >
                  {t('ticketing.back')}
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <Button
                type="submit"
                form="checkout"
                className="w-full"
                size="lg"
                disabled={
                  orderMutation.isPending ||
                  !consents.terms ||
                  !consents.privacy ||
                  Boolean(quote?.admission_required && !admissionVerified)
                }
              >
                {orderMutation.isPending ? t('ticketing.submitting') : t('ticketing.submit')}
              </Button>
              <Button
                variant="ghost"
                className="w-full"
                // Retour au point d'entrée, jamais vers une autre porte :
                // celui venu pour un billet ne doit pas découvrir les tables
                // en revenant en arrière.
                onClick={() =>
                  setStep(entry === 'table' ? 'table' : entry === 'food' ? 'food' : 'select')
                }
              >
                {t('ticketing.back')}
              </Button>
            </div>
          )}
        </QuoteSummary>
      </div>
    </Section>
    </PublicLayout>
  )
}

/** Les refus de la règle « pas de repas sans entrée », dans la langue du client. */
const ADMISSION_ERRORS: Record<string, string> = {
  FOOD_NEEDS_ADMISSION: 'admission.required',
  ADMISSION_ORDER_NOT_FOUND: 'admission.notFound',
  ADMISSION_ORDER_INACTIVE: 'admission.inactive',
  ADMISSION_ORDER_UNPAID: 'admission.unpaid',
  ADMISSION_ORDER_NO_ENTRY: 'admission.noEntry',
}

/**
 * « Vous avez déjà votre billet ou votre table ? »
 *
 * En ligne, on ne commande pas à manger sans entrée. Celui qui a déjà la
 * sienne cite sa commande — numéro et même adresse e-mail — et ses plats s'y
 * **ajoutent** : même numéro, même code d'entrée, aucune seconde commande
 * (`appended`, dit par le serveur). Les autres ajoutent un billet ou une
 * table à ce panier.
 * Le bouton de validation reste fermé tant que l'un ou l'autre manque.
 */
function AdmissionBlock({
  number,
  onNumberChange,
  verified,
  appended,
  error,
  checking,
  onVerify,
  onAddTicket,
  onAddTable,
}: {
  number: string
  onNumberChange: (value: string) => void
  verified: string | null
  appended: boolean
  error: string | null
  checking: boolean
  onVerify: () => void
  onAddTicket: (() => void) | null
  onAddTable: (() => void) | null
}) {
  const { t } = useTranslation()
  return (
    <section
      aria-labelledby="admission-title"
      className="rounded-lg border border-ember-500 bg-bg-overlay p-4 sm:p-5"
    >
      <h3 id="admission-title" className="font-display text-heading-sm text-content-primary">
        {t('admission.title')}
      </h3>
      <p className="mt-2 text-sm text-content-secondary">{t('admission.body')}</p>

      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end">
        <div className="min-w-0 flex-1">
          <Field label={t('admission.numberLabel')}>
            <TextInput
              value={number}
              autoComplete="off"
              autoCapitalize="characters"
              placeholder="MN26-000123"
              aria-invalid={error ? true : undefined}
              onChange={(changeEvent) => onNumberChange(changeEvent.target.value)}
              onKeyDown={(keyEvent) => {
                // Entrée vérifie le numéro au lieu d'envoyer tout le formulaire.
                if (keyEvent.key === 'Enter') {
                  keyEvent.preventDefault()
                  onVerify()
                }
              }}
            />
          </Field>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={onVerify}
          disabled={checking || !number.trim()}
          className="w-full sm:w-auto"
        >
          {checking ? t('admission.checking') : t('admission.verify')}
        </Button>
      </div>

      {verified ? (
        <p role="status" className="mt-3 text-sm text-state-success">
          {appended
            ? t('admission.appended', { number: verified })
            : t('admission.verified', { number: verified })}
        </p>
      ) : error ? (
        <p role="alert" className="mt-3 text-sm text-state-danger">
          {error}
        </p>
      ) : (
        <p className="mt-3 text-caption text-content-muted">{t('admission.sameEmail')}</p>
      )}

      {!verified && (onAddTicket || onAddTable) ? (
        <div className="mt-5 border-t border-line-subtle pt-4">
          <p className="text-sm text-content-secondary">{t('admission.noEntryYet')}</p>
          <div className="mt-3 flex flex-col gap-2 sm:flex-row">
            {onAddTicket ? (
              <Button
                type="button"
                variant="outline"
                onClick={onAddTicket}
                className="w-full sm:w-auto"
              >
                {t('admission.addTicket')}
              </Button>
            ) : null}
            {onAddTable ? (
              <Button
                type="button"
                variant="ghost"
                onClick={onAddTable}
                className="w-full sm:w-auto"
              >
                {t('admission.addTable')}
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
    </section>
  )
}

/** Le refus d'un champ, sous le champ : c'est là qu'on le cherche. */
function FieldError({ message }: { message: string | undefined }) {
  if (!message) return null
  return (
    <p role="alert" className="mt-1.5 text-sm text-state-danger">
      {message}
    </p>
  )
}
